import { useEffect, useState, useCallback } from "react";
import {
  fetchServiceOrders, createServiceOrder, updateServiceOrder, deleteServiceOrder,
  fetchClients, fetchVehicles, fetchParts, fetchServiceCatalog,
  downloadServiceOrderPdf, fmtMoney, fmtDate, createFinancial, emitNfse,
} from "@/lib/api";
import { PageHeader, EmptyState, StatusChip } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Plus, PencilSimple, Trash, FilePdf, Eye, Wrench, Package, Receipt } from "@phosphor-icons/react";
import { toast } from "sonner";

const newItem = (preset = {}) => ({
  id: crypto.randomUUID(),
  nome: "",
  descricao: "",
  quantidade: 1,
  preco_unitario: 0,
  aprovado: false,
  ...preset,
});

const emptyOS = {
  client_id: "", vehicle_id: "", status: "aberta",
  km_atual: 0, observacoes: "", servicos: [], pecas: [], desconto: 0,
};

export default function ServiceOrders() {
  const [list, setList] = useState([]);
  const [clients, setClients] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [parts, setParts] = useState([]);
  const [services, setServices] = useState([]);
  const [tab, setTab] = useState("todas");
  const [open, setOpen] = useState(false);
  const [view, setView] = useState(null);
  const [form, setForm] = useState(emptyOS);
  const [editId, setEditId] = useState(null);

  const reloadAll = useCallback(
    () => Promise.all([fetchServiceOrders(), fetchClients(), fetchVehicles(), fetchParts(), fetchServiceCatalog()])
      .then(([o, c, v, p, s]) => { setList(o); setClients(c); setVehicles(v); setParts(p); setServices(s); }),
    []
  );

  useEffect(() => { reloadAll(); }, [reloadAll]);

  const clientName = (id) => clients.find((c) => c.id === id)?.nome || "—";
  const vehicleStr = (id) => {
    const v = vehicles.find((x) => x.id === id);
    return v ? `${v.placa} • ${v.modelo}` : "—";
  };
  const filteredVehicles = form.client_id ? vehicles.filter((v) => v.client_id === form.client_id) : vehicles;

  const filtered = list.filter((o) => tab === "todas" ? true : o.status === tab);

  // Item editing helpers
  const updateItem = (kind, idx, patch) => {
    setForm((f) => {
      const arr = [...f[kind]];
      arr[idx] = { ...arr[idx], ...patch };
      return { ...f, [kind]: arr };
    });
  };
  const addItem = (kind) => setForm((f) => ({ ...f, [kind]: [...f[kind], newItem()] }));
  const removeItem = (kind, idx) => setForm((f) => ({ ...f, [kind]: f[kind].filter((_, i) => i !== idx) }));

  const pickService = (idx, sid) => {
    const s = services.find((x) => x.id === sid);
    if (s) updateItem("servicos", idx, { nome: s.nome, descricao: s.descricao || "", preco_unitario: s.preco_padrao });
  };
  const pickPart = (idx, pid) => {
    const p = parts.find((x) => x.id === pid);
    if (p) updateItem("pecas", idx, { nome: p.nome, descricao: p.descricao || "", preco_unitario: p.preco_unitario, part_id: p.id });
  };

  const calcTotals = (data) => {
    const sumApr = (arr) => arr.filter((i) => i.aprovado).reduce((s, i) => s + (i.quantidade * i.preco_unitario), 0);
    const sumNAp = (arr) => arr.filter((i) => !i.aprovado).reduce((s, i) => s + (i.quantidade * i.preco_unitario), 0);
    const sa = sumApr(data.servicos || []);
    const pa = sumApr(data.pecas || []);
    const sn = sumNAp(data.servicos || []);
    const pn = sumNAp(data.pecas || []);
    const totalApr = Math.max(sa + pa - (data.desconto || 0), 0);
    return { sa, pa, sn, pn, totalApr, totalNAp: sn + pn };
  };

  const onEdit = (o) => {
    setEditId(o.id);
    setForm({
      client_id: o.client_id, vehicle_id: o.vehicle_id, status: o.status,
      km_atual: o.km_atual || 0, observacoes: o.observacoes || "",
      servicos: o.servicos || [], pecas: o.pecas || [], desconto: o.desconto || 0,
    });
    setOpen(true);
  };

  const onDelete = async (id) => {
    if (!window.confirm("Excluir esta ordem de serviço?")) return;
    await deleteServiceOrder(id);
    toast.success("OS excluída");
    reloadAll();
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.client_id || !form.vehicle_id) { toast.error("Cliente e veículo são obrigatórios"); return; }
    const payload = {
      ...form,
      km_atual: parseInt(form.km_atual || 0, 10),
      desconto: parseFloat(form.desconto || 0),
      servicos: form.servicos.map((i) => ({ ...i, quantidade: parseFloat(i.quantidade || 1), preco_unitario: parseFloat(i.preco_unitario || 0) })),
      pecas: form.pecas.map((i) => ({ ...i, quantidade: parseFloat(i.quantidade || 1), preco_unitario: parseFloat(i.preco_unitario || 0) })),
    };
    try {
      let saved;
      if (editId) saved = await updateServiceOrder(editId, payload);
      else saved = await createServiceOrder(payload);

      // If finalizing, create financial receivable for approved total
      if (payload.status === "finalizada") {
        const t = calcTotals(payload);
        if (t.totalApr > 0) {
          await createFinancial({
            tipo: "receber",
            descricao: `OS Nº ${saved.numero} - ${clientName(saved.client_id)}`,
            valor: t.totalApr,
            categoria: "Ordem de Serviço",
            data_vencimento: new Date().toISOString().slice(0, 10),
            status: "pendente",
            ordem_servico_id: saved.id,
          });
        }
      }

      toast.success(editId ? "OS atualizada" : "OS criada");
      setOpen(false); setForm(emptyOS); setEditId(null);
      reloadAll();
    } catch (err) {
      toast.error("Erro ao salvar");
    }
  };

  const totals = calcTotals(form);

  return (
    <div data-testid="orders-page">
      <PageHeader
        title="Ordens de Serviço"
        subtitle="Gestão completa de OS com aprovação individual de serviços e peças."
        actions={
          <Button
            data-testid="new-os-btn"
            onClick={() => { setForm(emptyOS); setEditId(null); setOpen(true); }}
            className="bg-[#001E50] hover:bg-[#001740] text-white"
          >
            <Plus size={16} weight="bold" className="mr-1.5" /> Nova OS
          </Button>
        }
      />

      {/* Status tabs */}
      <Tabs value={tab} onValueChange={setTab} className="mb-4">
        <TabsList>
          <TabsTrigger value="todas">Todas</TabsTrigger>
          <TabsTrigger value="aberta">Abertas</TabsTrigger>
          <TabsTrigger value="em_andamento">Em andamento</TabsTrigger>
          <TabsTrigger value="finalizada">Finalizadas</TabsTrigger>
          <TabsTrigger value="cancelada">Canceladas</TabsTrigger>
        </TabsList>
      </Tabs>

      {/* List */}
      <div className="bg-white border border-[#E2E8F0] rounded-md">
        {filtered.length === 0 ? (
          <EmptyState title="Nenhuma OS encontrada" message="Crie uma nova ordem de serviço." />
        ) : (
          <Table>
            <TableHeader className="bg-[#F8FAFC]">
              <TableRow>
                <TableHead className="w-[80px]">Nº</TableHead>
                <TableHead>Cliente</TableHead>
                <TableHead>Veículo</TableHead>
                <TableHead>Aberta</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Total Aprovado</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((o, i) => {
                const t = calcTotals(o);
                return (
                  <TableRow key={o.id} className={i % 2 ? "bg-[#F8FAFC]" : ""} data-testid={`os-row-${o.id}`}>
                    <TableCell className="font-mono font-bold text-[#001E50]">{String(o.numero).padStart(4, "0")}</TableCell>
                    <TableCell className="font-medium">{clientName(o.client_id)}</TableCell>
                    <TableCell className="text-[#475569]">{vehicleStr(o.vehicle_id)}</TableCell>
                    <TableCell className="text-[#475569] text-sm">{fmtDate(o.data_abertura)}</TableCell>
                    <TableCell><StatusChip status={o.status} /></TableCell>
                    <TableCell className="text-right tabular-nums font-semibold">{fmtMoney(t.totalApr)}</TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="icon" onClick={() => setView(o)} data-testid={`view-os-${o.id}`}>
                        <Eye size={16} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        data-testid={`pdf-os-${o.id}`}
                        title="Baixar PDF"
                        onClick={async () => {
                          try {
                            await downloadServiceOrderPdf(o.id, o.numero);
                          } catch (e) {
                            toast.error("Erro ao baixar PDF");
                          }
                        }}
                      >
                        <FilePdf size={16} className="text-[#00B0F0]" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => onEdit(o)} data-testid={`edit-os-${o.id}`}>
                        <PencilSimple size={16} />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => onDelete(o.id)} data-testid={`delete-os-${o.id}`}>
                        <Trash size={16} className="text-red-500" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>

      {/* View dialog */}
      <Dialog open={!!view} onOpenChange={(v) => !v && setView(null)}>
        <DialogContent className="sm:max-w-[760px] max-h-[90vh] overflow-y-auto">
          {view && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading flex items-center gap-3">
                  OS Nº <span className="font-mono text-[#001E50]">{String(view.numero).padStart(4, "0")}</span>
                  <StatusChip status={view.status} />
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-[#475569]">Cliente</div>
                    <div className="font-medium">{clientName(view.client_id)}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-[#475569]">Veículo</div>
                    <div className="font-medium">{vehicleStr(view.vehicle_id)}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-[#475569]">Aberta</div>
                    <div>{fmtDate(view.data_abertura)}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-[#475569]">KM</div>
                    <div>{(view.km_atual || 0).toLocaleString("pt-BR")}</div>
                  </div>
                </div>

                <ItemListView title="Serviços" items={view.servicos || []} icon={Wrench} />
                <ItemListView title="Peças" items={view.pecas || []} icon={Package} />

                {view.observacoes && (
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-[#475569] mb-1">Observações</div>
                    <p className="text-sm whitespace-pre-line">{view.observacoes}</p>
                  </div>
                )}

                <div className="flex flex-wrap justify-end gap-2 pt-2">
                  <Button
                    variant="outline"
                    data-testid={`emit-nfse-${view.id}`}
                    onClick={async () => {
                      try {
                        const res = await emitNfse(view.id);
                        if (res.status === "erro") {
                          toast.error(res.mensagem || "Erro ao emitir NFS-e");
                        } else {
                          toast.success(`NFS-e enviada (ref: ${res.reference}). Veja em Notas Fiscais.`);
                        }
                      } catch (e) {
                        toast.error(e?.response?.data?.detail || "Erro ao emitir NFS-e");
                      }
                    }}
                  >
                    <Receipt size={16} weight="bold" className="mr-1.5" /> Emitir NFS-e
                  </Button>
                  <Button
                    className="bg-[#00B0F0] hover:bg-[#0099D6] text-white"
                    data-testid={`download-pdf-${view.id}`}
                    onClick={async () => {
                      try {
                        await downloadServiceOrderPdf(view.id, view.numero);
                      } catch (e) {
                        toast.error("Erro ao baixar PDF");
                      }
                    }}
                  >
                    <FilePdf size={16} weight="bold" className="mr-1.5" /> Baixar PDF
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Create / Edit dialog */}
      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setForm(emptyOS); setEditId(null); } }}>
        <DialogContent className="sm:max-w-[920px] max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">{editId ? "Editar Ordem de Serviço" : "Nova Ordem de Serviço"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={onSubmit} className="space-y-5">
            {/* Header */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="md:col-span-2">
                <Label>Cliente *</Label>
                <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v, vehicle_id: "" })}>
                  <SelectTrigger data-testid="os-client"><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent>
                    {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2">
                <Label>Veículo *</Label>
                <Select value={form.vehicle_id} onValueChange={(v) => setForm({ ...form, vehicle_id: v })}>
                  <SelectTrigger data-testid="os-vehicle"><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent>
                    {filteredVehicles.map((v) => <SelectItem key={v.id} value={v.id}>{v.placa} — {v.modelo}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="os-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="aberta">Aberta</SelectItem>
                    <SelectItem value="em_andamento">Em andamento</SelectItem>
                    <SelectItem value="finalizada">Finalizada</SelectItem>
                    <SelectItem value="cancelada">Cancelada</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>KM atual</Label>
                <Input data-testid="os-km" type="number" value={form.km_atual} onChange={(e) => setForm({ ...form, km_atual: e.target.value })} />
              </div>
              <div>
                <Label>Desconto (R$)</Label>
                <Input data-testid="os-desc" type="number" step="0.01" value={form.desconto} onChange={(e) => setForm({ ...form, desconto: e.target.value })} />
              </div>
            </div>

            {/* Services */}
            <ItemEditor
              title="Serviços"
              items={form.servicos}
              icon={Wrench}
              onAdd={() => addItem("servicos")}
              onRemove={(idx) => removeItem("servicos", idx)}
              onChange={(idx, patch) => updateItem("servicos", idx, patch)}
              onPickPreset={(idx, sid) => pickService(idx, sid)}
              presets={services}
              presetLabel="Catálogo"
            />

            {/* Parts */}
            <ItemEditor
              title="Peças"
              items={form.pecas}
              icon={Package}
              onAdd={() => addItem("pecas")}
              onRemove={(idx) => removeItem("pecas", idx)}
              onChange={(idx, patch) => updateItem("pecas", idx, patch)}
              onPickPreset={(idx, pid) => pickPart(idx, pid)}
              presets={parts}
              presetLabel="Estoque"
            />

            {/* Observations */}
            <div>
              <Label>Observações</Label>
              <Textarea data-testid="os-obs" rows={3} value={form.observacoes} onChange={(e) => setForm({ ...form, observacoes: e.target.value })} />
            </div>

            {/* Totals */}
            <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-md p-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#475569]">Serv. Aprov.</div>
                <div className="font-semibold tabular-nums">{fmtMoney(totals.sa)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#475569]">Peças Aprov.</div>
                <div className="font-semibold tabular-nums">{fmtMoney(totals.pa)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#475569]">Não Aprov.</div>
                <div className="font-semibold tabular-nums text-[#475569]">{fmtMoney(totals.totalNAp)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#00B0F0]">Total a Pagar</div>
                <div className="font-heading text-xl font-semibold text-[#001E50] tabular-nums" data-testid="os-total">{fmtMoney(totals.totalApr)}</div>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
              <Button type="submit" data-testid="save-os-btn" className="bg-[#001E50] hover:bg-[#001740] text-white">Salvar OS</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ItemListView({ title, items, icon: Icon }) {
  const aprovados = items.filter((i) => i.aprovado);
  const naprovados = items.filter((i) => !i.aprovado);
  const Section = ({ label, list, accent }) => list.length > 0 && (
    <div className="border border-[#E2E8F0] rounded-md overflow-hidden">
      <div className={`px-4 py-2 text-[11px] uppercase tracking-[0.2em] ${accent ? "bg-[#10B981] text-white" : "bg-[#F1F5F9] text-[#475569]"}`}>
        {label}
      </div>
      <ul className="divide-y divide-[#E2E8F0]">
        {list.map((i) => {
          const sub = (i.quantidade * i.preco_unitario).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
          return (
            <li key={i.id} className="flex items-center justify-between px-4 py-2 text-sm">
              <div>
                <div className="font-medium">{i.nome}</div>
                {i.descricao && <div className="text-xs text-[#475569]">{i.descricao}</div>}
              </div>
              <div className="text-right tabular-nums">
                <div>{i.quantidade} × {(i.preco_unitario || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}</div>
                <div className="font-semibold">{sub}</div>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
  return (
    <div>
      <div className="flex items-center gap-2 mb-2 text-[11px] uppercase tracking-[0.2em] text-[#475569]">
        <Icon size={14} weight="duotone" /> {title}
      </div>
      <div className="space-y-3">
        <Section label="Aprovados" list={aprovados} accent />
        <Section label="Não aprovados (orçamento)" list={naprovados} />
        {items.length === 0 && <p className="text-sm text-[#475569]">Nenhum item.</p>}
      </div>
    </div>
  );
}

function ItemEditor({ title, items, icon: Icon, onAdd, onRemove, onChange, onPickPreset, presets, presetLabel }) {
  return (
    <div className="border border-[#E2E8F0] rounded-md">
      <div className="flex items-center justify-between px-4 py-3 border-b border-[#E2E8F0] bg-[#F8FAFC]">
        <div className="flex items-center gap-2 text-sm font-semibold text-[#001E50]">
          <Icon size={16} weight="duotone" /> {title}
        </div>
        <Button type="button" size="sm" variant="outline" onClick={onAdd} data-testid={`add-${title.toLowerCase()}-btn`}>
          <Plus size={14} className="mr-1" /> Adicionar
        </Button>
      </div>
      {items.length === 0 ? (
        <div className="p-4 text-sm text-[#475569]">Nenhum item adicionado.</div>
      ) : (
        <div className="p-3 space-y-3">
          {items.map((it, idx) => (
            <div key={it.id} className="grid grid-cols-12 gap-2 items-end p-3 rounded-md border border-[#E2E8F0]">
              {presets.length > 0 && (
                <div className="col-span-12 md:col-span-3">
                  <Label className="text-[10px] uppercase tracking-wider text-[#475569]">{presetLabel}</Label>
                  <Select value="" onValueChange={(v) => onPickPreset(idx, v)}>
                    <SelectTrigger><SelectValue placeholder="Selecionar..." /></SelectTrigger>
                    <SelectContent>
                      {presets.map((p) => <SelectItem key={p.id} value={p.id}>{p.nome}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="col-span-12 md:col-span-3">
                <Label className="text-[10px] uppercase tracking-wider text-[#475569]">Nome *</Label>
                <Input value={it.nome} onChange={(e) => onChange(idx, { nome: e.target.value })} />
              </div>
              <div className="col-span-12 md:col-span-2">
                <Label className="text-[10px] uppercase tracking-wider text-[#475569]">Qtd</Label>
                <Input type="number" step="0.01" value={it.quantidade} onChange={(e) => onChange(idx, { quantidade: e.target.value })} />
              </div>
              <div className="col-span-6 md:col-span-2">
                <Label className="text-[10px] uppercase tracking-wider text-[#475569]">Vlr unit.</Label>
                <Input type="number" step="0.01" value={it.preco_unitario} onChange={(e) => onChange(idx, { preco_unitario: e.target.value })} />
              </div>
              <div className="col-span-4 md:col-span-1 flex items-center justify-center pb-2">
                <div className="flex flex-col items-center gap-1">
                  <Switch
                    checked={it.aprovado}
                    onCheckedChange={(v) => onChange(idx, { aprovado: v })}
                    data-testid={`approve-${title.toLowerCase()}-${idx}`}
                  />
                  <span className={`text-[9px] uppercase tracking-wider ${it.aprovado ? "text-[#10B981]" : "text-[#475569]"}`}>
                    {it.aprovado ? "Aprov." : "N/Aprov."}
                  </span>
                </div>
              </div>
              <div className="col-span-2 md:col-span-1 flex justify-end pb-2">
                <Button type="button" variant="ghost" size="icon" onClick={() => onRemove(idx)}>
                  <Trash size={16} className="text-red-500" />
                </Button>
              </div>
              <div className="col-span-12">
                <Label className="text-[10px] uppercase tracking-wider text-[#475569]">Descrição</Label>
                <Input value={it.descricao} onChange={(e) => onChange(idx, { descricao: e.target.value })} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
